import React, {Fragment} from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  makeStyles,
  Typography,
} from '@material-ui/core';
import {ExpandMore as ExpandMoreIcon} from '@material-ui/icons';
import './Chart.css';
import {meterUnitConversationRateAction} from '../../utils/meterUnitConversion';
import {ChartViewToggle} from './ChartViewToggle';
import {accordionA11yProps} from '../utils/accordionA11yProps';
import {AverageUsageSummary} from './AverageUsageSummary';

const EXPECTED_COLOR = '#F9F935';
const BASELINE_COLOR = 'black';
const EXPERIMENT_COLOR = 'white';

const average = (nums) => {
  const finite = nums.filter((n) => Number.isFinite(n));
  if (finite.length === 0) return null;
  return finite.reduce((sum, n) => sum + n, 0) / finite.length;
};

export const DashboardDailyUsageBarChart = ({
  meter,
  view,
  onViewChange,
  expanded,
  onToggleExpand,
}) => {
  const useStyles = makeStyles(({palette, breakpoints}) => ({
    usageChart: {
      width: '100%',
      height: '75vh',
      backgroundColor: palette.meterTypeColor,
      // Keep the colored background full-bleed; only cap the height on desktop.
      [breakpoints.up('md')]: {
        height: '60vh',
        maxHeight: '550px',
      },
    },
    // The 3-bar chart stretches awkwardly wide on desktop; cap and center the
    // chart itself while the background above spans the full container width.
    chartArea: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      [breakpoints.up('md')]: {
        maxWidth: '700px',
        margin: '0 auto',
      },
    },
    // ResponsiveContainer needs a sized parent; this fills whatever height is
    // left after the summary box above it.
    chartBody: {
      flex: 1,
      minHeight: 0,
    },
    heading: {
      fontSize: '1.7em',
    },
    usageChartHeader: {
      color: palette.meterTypeTextColor,
      backgroundColor: palette.meterTypeColor,
      '& svg': {
        color: palette.meterTypeTextColor,
      },
      textTransform: 'capitalize',
    },
  }));

  const classes = useStyles(meter);
  const a11y = accordionA11yProps({
    id: 'usage-chart',
    expanded,
    label: expanded ? 'collapse usage chart' : 'expand usage chart',
  });

  const validReadings = (meter.readings || []).filter((r) => !r.deleted);
  const experimentStart = meter.experimentStart
    ? new Date(meter.experimentStart).valueOf()
    : undefined;

  const baselineDailies = [];
  const experimentDailies = [];
  for (const r of validReadings) {
    const t = new Date(r.time).valueOf();
    const daily = Number(r.dailyUsage);
    if (!Number.isFinite(daily)) continue;
    if (experimentStart && t > experimentStart) {
      experimentDailies.push(daily);
    } else {
      baselineDailies.push(daily);
    }
  }

  const baselineAverage = average(baselineDailies);
  const experimentAverage = average(experimentDailies);

  let expectedValue = null;
  if (meter.expectedUsage != null && meter.expectedUsage !== '') {
    const converted =
      meter.unit && meter.expectedUsageUnit
        ? meterUnitConversationRateAction(
            meter.unit,
            meter.expectedUsageUnit,
            meter.expectedUsage,
          )
        : meter.expectedUsage;
    const num = Number(converted);
    if (Number.isFinite(num)) expectedValue = num;
  }

  const data = [
    {name: 'Expected', value: expectedValue, fill: EXPECTED_COLOR},
    {name: 'Baseline', value: baselineAverage, fill: BASELINE_COLOR},
    {name: 'Experiment', value: experimentAverage, fill: EXPERIMENT_COLOR},
  ].map((d) => ({...d, value: Number.isFinite(d.value) ? d.value : 0}));

  return (
    <Accordion expanded={expanded} onChange={onToggleExpand}>
      <AccordionSummary
        className={classes.usageChartHeader}
        expandIcon={<ExpandMoreIcon />}
        {...a11y.summaryProps}
      >
        <Typography
          className={classes.heading}
        >{`${meter.type} Usage Chart`}</Typography>
        <ChartViewToggle view={view} onChange={onViewChange} />
      </AccordionSummary>
      <AccordionDetails className={classes.usageChart} {...a11y.detailsProps}>
        <Fragment>
          <div className={classes.chartArea}>
            <AverageUsageSummary
              baselineAverage={baselineAverage}
              experimentAverage={experimentAverage}
              unit={meter.unit}
              placement="above"
            />
            <div className={classes.chartBody}>
              <ResponsiveContainer>
                <BarChart
                  data={data}
                  margin={{top: 15, right: 30, left: 20, bottom: 5}}
                >
                  <CartesianGrid
                    vertical={false}
                    stroke="white"
                    strokeOpacity="0.6"
                    strokeDasharray="2 2 2"
                  />

                  <XAxis
                    dataKey="name"
                    stroke="white"
                    style={{fontSize: '1em'}}
                    tickLine={{stroke: 'white'}}
                    axisLine={{stroke: 'white'}}
                  >
                    <Label
                      value="Average daily usage"
                      fill="white"
                      position="insideBottomRight"
                      offset={0}
                    />
                  </XAxis>

                  <YAxis
                    type="number"
                    stroke="white"
                    style={{fontSize: '1em'}}
                    domain={[0, 'auto']}
                    tickLine={false}
                    padding={{top: 20, bottom: 30}}
                    axisLine={false}
                  >
                    <Label
                      value={meter.unit}
                      angle={-90}
                      position="insideLeft"
                      fill="white"
                    />
                  </YAxis>

                  <Bar dataKey="value" isAnimationActive={false}>
                    {data.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Fragment>
      </AccordionDetails>
    </Accordion>
  );
};
